import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FileText,
  MessageSquare,
  Mic,
  MicOff,
  Paperclip,
  RotateCcw,
  Send,
  Sparkles,
  Volume2,
  VolumeX,
  X
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { chatBrainAi, ingestBrainDocAi } from '../../services/api/implementation/impl';
import { AiCardShell, AiCreditsBadge, AiCreditsNotice, formatAiApiError } from './AiUiShared';
import { getBrainChatSurface } from './brainChatSurfaces';
import { buildOpsAiReturnTo, openBoqWithReturn } from './opsAiNavigation';
import { clearOpsAiDraft, loadOpsAiDraft, saveOpsAiDraft } from './opsAiDraftStore';
import OpsTemplateProposalPreview from './OpsTemplateProposalPreview';
import OpsTemplateQuestionPanel, { formatAnswersMessage } from './OpsTemplateQuestionPanel';
import useSpeechDictation from './useSpeechDictation';
import useSpeechTTS from './useSpeechTTS';
import { extractOpsDocText } from './extractOpsDocText';

/**
 * Ops-specific Brain chat: voice, guided questions, editable proposal, Apply.
 */
export default function OpsTemplateAiChat({
  open,
  onClose,
  projectId = null,
  contextHints = null,
  onApply = null,
  isApplying = false
}) {
  const surfaceId = 'ops_template_chat';
  const config = getBrainChatSurface(surfaceId);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [interimInput, setInterimInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [docLoading, setDocLoading] = useState(false);
  const [error, setError] = useState('');
  const [aiCredits, setAiCredits] = useState(null);
  const [applyPayload, setApplyPayload] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answeredIds, setAnsweredIds] = useState([]);
  const [questionsDismissed, setQuestionsDismissed] = useState(false);
  const [attachedDocs, setAttachedDocs] = useState([]);
  const [restoredDocNames, setRestoredDocNames] = useState([]);
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastSpokenKeyRef = useRef('');
  const navigate = useNavigate();
  const location = useLocation();

  const documentContext = useMemo(() => {
    if (!attachedDocs.length) return null;
    return attachedDocs
      .map((doc) => `--- ${doc.fileName} ---\n${doc.text}`)
      .join('\n\n')
      .slice(0, 14000);
  }, [attachedDocs]);

  const hintsWithAnswers = useMemo(() => {
    const base = contextHints && typeof contextHints === 'object' ? { ...contextHints } : {};
    if (answeredIds.length) {
      base.answeredQuestionIds = answeredIds;
    }
    if (documentContext) {
      base.documentContext = documentContext;
      base.documentNames = attachedDocs.map((d) => d.fileName);
    }
    return Object.keys(base).length ? base : null;
  }, [contextHints, answeredIds, documentContext, attachedDocs]);

  const tts = useSpeechTTS({ lang: 'en-IN' });

  const appendToInput = useCallback((text) => {
    setInput((prev) => {
      const next = prev.trim() ? `${prev.trim()} ${text}` : text;
      return next;
    });
    setInterimInput('');
  }, []);

  const dictation = useSpeechDictation({
    lang: 'en-IN',
    onFinalTranscript: appendToInput,
    onInterimTranscript: setInterimInput
  });

  const welcomeMessage = useMemo(
    () => ({
      role: 'assistant',
      localOnly: true,
      content:
        config?.subtitle ||
        'Describe the template you want — type, use the mic, or attach an SOP/PDF. I will propose tasks and ask about anything missing.'
    }),
    [config?.subtitle]
  );

  const resetConversation = useCallback(
    ({ clearDraft = true } = {}) => {
      if (clearDraft) clearOpsAiDraft();
      setMessages([welcomeMessage]);
      setInput('');
      setInterimInput('');
      setError('');
      setApplyPayload(null);
      setQuestions([]);
      setAnsweredIds([]);
      setQuestionsDismissed(false);
      setAttachedDocs([]);
      setRestoredDocNames([]);
      lastSpokenKeyRef.current = '';
      tts.cancel();
      dictation.stop();
    },
    [welcomeMessage, tts, dictation]
  );

  useEffect(() => {
    if (!open) {
      tts.cancel();
      dictation.stop();
      setInterimInput('');
      return;
    }

    const draft = loadOpsAiDraft({ surfaceId, projectId });
    if (draft) {
      setMessages(
        draft.messages?.length
          ? [
              ...draft.messages,
              {
                role: 'assistant',
                localOnly: true,
                content:
                  'Picked up your earlier draft — proposal and answers are still here. Use “Start new” to begin from scratch.'
              }
            ]
          : [welcomeMessage]
      );
      setApplyPayload(draft.applyPayload ?? null);
      setQuestions(draft.questions ?? []);
      setAnsweredIds(draft.answeredIds ?? []);
      setQuestionsDismissed(Boolean(draft.questionsDismissed));
      setRestoredDocNames(draft.docNames ?? []);
      setAttachedDocs([]);
      setInput('');
      setInterimInput('');
      setError('');
      lastSpokenKeyRef.current = '';
      tts.cancel();
      dictation.stop();
      return;
    }

    resetConversation({ clearDraft: false });
  }, [open, surfaceId, projectId]);

  const persistDraft = useCallback(() => {
    saveOpsAiDraft({
      surfaceId,
      projectId,
      messages,
      applyPayload,
      questions,
      answeredIds,
      questionsDismissed,
      docNames: [...restoredDocNames, ...attachedDocs.map((d) => d.fileName)]
    });
  }, [
    surfaceId,
    projectId,
    messages,
    applyPayload,
    questions,
    answeredIds,
    questionsDismissed,
    restoredDocNames,
    attachedDocs
  ]);

  const handleOpenBoq = useCallback(() => {
    persistDraft();
    dictation.stop();
    tts.cancel();
    onClose?.();
    openBoqWithReturn(navigate, location, projectId);
  }, [persistDraft, dictation, tts, onClose, navigate, location, projectId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, questions, applyPayload, attachedDocs]);

  const ttsSpeak = tts.speak;
  const ttsEnabled = tts.enabled;

  useEffect(() => {
    if (!open || !questions.length || questionsDismissed || !ttsEnabled) return;
    const key = questions.map((q) => q.id).join('|');
    if (key === lastSpokenKeyRef.current) return;
    lastSpokenKeyRef.current = key;
    const spoken = questions
      .slice(0, 3)
      .map((q) => q.question)
      .join('. ');
    if (spoken) ttsSpeak(spoken);
  }, [open, questions, questionsDismissed, ttsEnabled, ttsSpeak]);

  const runChat = async (nextMessages, extraHints = null) => {
    setLoading(true);
    setError('');
    try {
      const apiMessages = nextMessages
        .filter((m) => !m.localOnly && (m.role === 'user' || m.role === 'assistant'))
        .map((m) => ({ role: m.role, content: m.apiContent || m.content }));

      const mergedHints = {
        ...(hintsWithAnswers || {}),
        ...(applyPayload
          ? {
              currentProposal: {
                template_name: applyPayload.template_name,
                templateType: applyPayload.templateType,
                templates: applyPayload.templates
              }
            }
          : {}),
        ...(extraHints || {})
      };

      const data = await chatBrainAi({
        surface: config.id,
        vertical: config.vertical,
        projectId,
        messages: apiMessages,
        contextHints: Object.keys(mergedHints).length ? mergedHints : null
      });

      setAiCredits(data?.aiCredits ?? null);

      // Carry the request id on the proposal so that, if it is applied, the
      // backend can match what was saved against what was proposed. Without it
      // the Brain only ever learns from acceptance, never from the edits the
      // builder made on the way there.
      const apply = data?.applyPayload
        ? { ...data.applyPayload, requestId: data?.requestId ?? null }
        : null;
      setApplyPayload((prev) =>
        apply && Array.isArray(apply.templates) && apply.templates.length ? apply : prev
      );
      const effectiveApply =
        apply && Array.isArray(apply.templates) && apply.templates.length ? apply : applyPayload;

      const nextQuestions = Array.isArray(data?.clarificationQuestions)
        ? data.clarificationQuestions
        : Array.isArray(data?.artifacts?.clarificationQuestions)
          ? data.artifacts.clarificationQuestions
          : [];
      setQuestions(nextQuestions);
      setQuestionsDismissed(false);

      const count = Array.isArray(data?.proposedTasks)
        ? data.proposedTasks.length
        : Array.isArray(effectiveApply?.templates)
          ? effectiveApply.templates.length
          : 0;

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: data?.reply || 'No reply returned.',
          apiContent: JSON.stringify({
            reply: data?.reply || 'No reply returned.',
            templateType: effectiveApply?.templateType,
            template_name: effectiveApply?.template_name,
            proposedTasks: data?.proposedTasks?.length
              ? data.proposedTasks
              : effectiveApply?.templates || [],
            clarificationQuestions: nextQuestions,
            missingFields: data?.missingFields || data?.artifacts?.missingFields || []
          }),
          source: data?.source,
          ragUsed: data?.ragUsed,
          proposedCount: count,
          questionCount: nextQuestions.length
        }
      ]);
    } catch (err) {
      setError(formatAiApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!open || !config) return null;

  const sendText = async (text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed || loading) return;
    dictation.stop();
    setInterimInput('');
    const nextMessages = [...messages, { role: 'user', content: trimmed }];
    setMessages(nextMessages);
    setInput('');
    await runChat(nextMessages);
  };

  const send = () => sendText(input);

  const handleSubmitAnswers = async (answers) => {
    const ids = answers.map((a) => a.id);
    setAnsweredIds((prev) => [...new Set([...prev, ...ids])]);
    const content = formatAnswersMessage(answers);
    const nextMessages = [...messages, { role: 'user', content }];
    setMessages(nextMessages);
    setQuestions([]);
    await runChat(nextMessages, {
      answeredQuestions: answers,
      answeredQuestionIds: [...new Set([...answeredIds, ...ids])]
    });
  };

  const handleAttachDocs = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    setDocLoading(true);
    setError('');
    try {
      const extracted = [];
      for (const file of files.slice(0, 3)) {
        const result = await extractOpsDocText(file);
        if (result?.text) extracted.push(result);
      }
      if (!extracted.length) {
        setError('Could not read any attached documents');
        return;
      }
      setAttachedDocs((prev) => [...prev, ...extracted].slice(-3));

      const ingestResults = [];
      let latestCredits = null;
      for (const doc of extracted) {
        try {
          const ingested = await ingestBrainDocAi({
            content: doc.text,
            fileName: doc.fileName,
            projectId
          });
          if (ingested?.aiCredits) latestCredits = ingested.aiCredits;
          ingestResults.push({
            fileName: doc.fileName,
            docId: ingested?.docId,
            embedded: ingested?.embedded,
            reused: ingested?.reused
          });
        } catch (ingestErr) {
          // Chat still gets documentContext; durable RAG is best-effort.
          console.warn('SOP RAG ingest failed', ingestErr);
          ingestResults.push({
            fileName: doc.fileName,
            error: formatAiApiError(ingestErr) || 'ingest failed'
          });
        }
      }

      const ingestedOk = ingestResults.filter((r) => r.docId);
      const ingestFailed = ingestResults.filter((r) => r.error);
      const ragNote = ingestedOk.length
        ? ` Stored in RAG (${ingestedOk.map((d) => d.fileName).join(', ')}).`
        : '';
      const failNote = ingestFailed.length
        ? ` Chat context only for: ${ingestFailed.map((d) => d.fileName).join(', ')}.`
        : '';

      if (latestCredits) setAiCredits(latestCredits);

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          localOnly: true,
          content: `Attached ${extracted.map((d) => d.fileName).join(', ')} as SOP context for the next proposal.${ragNote}${failNote}`
        }
      ]);
    } catch (err) {
      setError(err?.message || 'Failed to read document');
    } finally {
      setDocLoading(false);
    }
  };

  const showQuestions = questions.length > 0 && !questionsDismissed;
  const canApply =
    typeof onApply === 'function' &&
    applyPayload &&
    Array.isArray(applyPayload.templates) &&
    applyPayload.templates.length > 0 &&
    (!showQuestions || !questions.some((q) => q?.required));

  const drawingRequiredCount = Array.isArray(applyPayload?.templates)
    ? applyPayload.templates.filter((t) => t.drawing_required).length
    : 0;

  return (
    <div className="fixed inset-0 z-[80] flex justify-end bg-black/30">
      <div className="flex h-full w-full max-w-lg flex-col bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-slate-100 px-4 py-3">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-indigo-600" />
              <h2 className="text-sm font-semibold text-slate-900">
                {config.title || 'Create with AI'}
              </h2>
            </div>
            <p className="mt-0.5 text-xs text-slate-500">{config.subtitle}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <AiCreditsBadge aiCredits={aiCredits} />
              {tts.supported ? (
                <button
                  type="button"
                  onClick={tts.toggleEnabled}
                  className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-600 hover:bg-slate-50"
                  title={tts.enabled ? 'Mute voice replies' : 'Enable voice replies'}
                >
                  {tts.enabled ? <Volume2 className="h-3 w-3" /> : <VolumeX className="h-3 w-3" />}
                  {tts.enabled ? 'Voice on' : 'Voice off'}
                </button>
              ) : null}
              {applyPayload?.templates?.length || messages.some((m) => !m.localOnly) ? (
                <button
                  type="button"
                  onClick={() => resetConversation()}
                  className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-600 hover:bg-slate-50"
                  title="Discard this draft and start a new proposal"
                >
                  <RotateCcw className="h-3 w-3" />
                  Start new
                </button>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              persistDraft();
              dictation.stop();
              tts.cancel();
              onClose?.();
            }}
            className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
            aria-label="Close chat"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
          <AiCreditsNotice aiCredits={aiCredits} />
          {messages.map((m, i) => (
            <div
              key={`${m.role}-${i}`}
              className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[92%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                  m.role === 'user'
                    ? 'bg-indigo-600 text-white'
                    : 'border border-slate-100 bg-slate-50 text-slate-800'
                }`}
              >
                {m.role === 'assistant' ? (
                  <span className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    <MessageSquare className="h-3 w-3" />
                    {m.source === 'ai' ? 'AI' : m.source === 'rules' ? 'Rules' : 'Assistant'}
                    {m.ragUsed ? ' · RAG' : ''}
                    {m.proposedCount > 0 ? ` · ${m.proposedCount} tasks` : ''}
                    {m.questionCount > 0 ? ` · ${m.questionCount} Qs` : ''}
                  </span>
                ) : null}
                <p className="whitespace-pre-wrap">{m.content}</p>
              </div>
            </div>
          ))}

          {attachedDocs.length || restoredDocNames.length ? (
            <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                Attached docs
              </p>
              <div className="flex flex-wrap gap-1.5">
                {restoredDocNames.map((name) => (
                  <span
                    key={`restored-${name}`}
                    className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500"
                    title="Already stored in AI knowledge (RAG) from this session"
                  >
                    <FileText className="h-3 w-3" />
                    {name} · in RAG
                  </span>
                ))}
                {attachedDocs.map((doc) => (
                  <span
                    key={doc.fileName}
                    className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-700"
                  >
                    <FileText className="h-3 w-3" />
                    {doc.fileName}
                    <button
                      type="button"
                      className="ml-0.5 text-slate-500 hover:text-red-600"
                      onClick={() =>
                        setAttachedDocs((prev) => prev.filter((d) => d.fileName !== doc.fileName))
                      }
                      aria-label={`Remove ${doc.fileName}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {showQuestions ? (
            <OpsTemplateQuestionPanel
              questions={questions}
              disabled={loading || isApplying}
              onSubmitAnswers={handleSubmitAnswers}
              onSkip={() => setQuestionsDismissed(true)}
            />
          ) : null}

          {applyPayload?.templates?.length ? (
            <OpsTemplateProposalPreview
              applyPayload={applyPayload}
              onChange={setApplyPayload}
              knownWorkTypes={
                Array.isArray(contextHints?.knownWorkTypes) ? contextHints.knownWorkTypes : []
              }
              showContinueAnyway={showQuestions}
              onContinueAnyway={() => setQuestionsDismissed(true)}
            />
          ) : null}

          {drawingRequiredCount > 0 ? (
            <div className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
              <p>
                {drawingRequiredCount} proposed task
                {drawingRequiredCount > 1 ? 's need' : ' needs'} drawings — BOQ quantities come from
                the BOQ &amp; BOM module.
              </p>
              <button
                type="button"
                onClick={handleOpenBoq}
                className="mt-1 font-semibold underline"
              >
                Open BOQ &amp; drawings
              </button>
              <p className="mt-1 text-[10px] text-sky-800">
                Your draft is saved — the back arrow there returns you to this proposal.
              </p>
            </div>
          ) : null}

          {loading || docLoading ? (
            <AiCardShell className="!p-3">
              <p className="text-xs text-slate-500">
                {docLoading ? 'Reading document…' : 'Thinking…'}
              </p>
            </AiCardShell>
          ) : null}
          {error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
              {error}
            </div>
          ) : null}
          {dictation.error ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {dictation.error}
            </div>
          ) : null}
          <div ref={bottomRef} />
        </div>

        {canApply ? (
          <div className="border-t border-slate-100 px-4 py-2">
            <button
              type="button"
              disabled={isApplying || loading}
              onClick={() => onApply(applyPayload)}
              className="w-full rounded-xl bg-teal-600 px-3 py-2 text-xs font-semibold text-white hover:bg-teal-700 disabled:opacity-50"
            >
              {isApplying
                ? 'Applying…'
                : `${config.applyLabel || 'Apply proposed tasks'} (${applyPayload.templates.length})`}
            </button>
          </div>
        ) : (
          <div className="border-t border-slate-50 px-4 py-1.5 text-[10px] text-slate-400">
            Describe a template set to get a proposal you can edit and apply
          </div>
        )}

        <div className="border-t border-slate-100 p-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt,.md,.csv,.json,.pdf,.xlsx,.xls,text/plain,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            multiple
            onChange={handleAttachDocs}
          />
          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading || docLoading}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40"
              aria-label="Attach SOP or document"
              title="Attach SOP / PDF / Excel"
            >
              <Paperclip className="h-4 w-4" />
            </button>
            {dictation.supported ? (
              <button
                type="button"
                onClick={dictation.toggle}
                disabled={loading}
                className={`inline-flex h-10 w-10 items-center justify-center rounded-xl border ${
                  dictation.listening
                    ? 'border-red-300 bg-red-50 text-red-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                } disabled:opacity-40`}
                aria-label={dictation.listening ? 'Stop microphone' : 'Start microphone'}
                title={dictation.listening ? 'Stop listening' : 'Speak your request'}
              >
                {dictation.listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </button>
            ) : null}
            <textarea
              value={
                dictation.listening && interimInput
                  ? `${input}${input.trim() ? ' ' : ''}${interimInput}`
                  : input
              }
              onChange={(e) => {
                setInput(e.target.value);
                setInterimInput('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={2}
              placeholder={
                dictation.listening ? 'Listening…' : config.placeholder || 'Describe the template…'
              }
              className="flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-300"
            />
            <button
              type="button"
              onClick={send}
              disabled={loading || !input.trim()}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-600 text-white disabled:opacity-40"
              aria-label="Send"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-1 text-[10px] text-slate-400">
            Attach SOP docs (TXT/MD/CSV/PDF/XLSX) for better proposals.
            {!dictation.supported ? ' Voice unavailable — typing still works.' : ''}
          </p>
        </div>
      </div>
    </div>
  );
}
